/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable prefer-template */
/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import { formatInTimeZone } from "date-fns-tz";
import moment from "moment";
import {
  ddMMyyyy,
  FORMAT_IN_ZONED,
  OperatingSystem,
  StorageClass,
} from "../constant";
import { DropDownResponse, OptionTypeBase } from "../types/common";
import { API_BASE_URL } from "@advapacs/advapacs-ui/src/config/constants";
import { format } from "date-fns";
import { isValid } from "../utils/date";

export const groupBy = (list: Array<any>, key: string): Array<any> => {
  return list.reduce((rv, x) => {
    const groupKey = x[key];
    rv[groupKey] = rv[groupKey] || [];
    rv[x[key]].push(x);
    return rv;
  }, {});
};

export const utcToLocal = (utc: string): Date => {
  const date = moment.utc(utc).format();
  return moment.utc(date).local().toDate();
};

export const utcToLocalDisplay = (utc: string, fmt?: string): string => {
  const date = moment.utc(utc).format();
  if (!fmt) {
    const now = new Date(2021, 7, 9);
    let newFormat = now.toLocaleDateString();
    newFormat = newFormat.replace("09", "DD");
    newFormat = newFormat.replace("08", "MM");
    newFormat = newFormat.replace("2021", "YYYY");
    newFormat += " HH:mm:ss";
    return moment.utc(date).local().format(newFormat);
  }
  return moment.utc(date).local().format(fmt);
};

export const getNumberOfDaysFromCurrentNumber = (date: string): number => {
  const currentDate = new Date();
  const futureDate = new Date(date);

  const oneDay = 1000 * 60 * 60 * 24;

  // Calculating the time difference between two dates
  const diffInTime = futureDate.getTime() - currentDate.getTime();

  // Calculating the no. of days between two dates
  const diffInDays = Math.round(diffInTime / oneDay);

  return diffInDays;
};

export const getNumberOfDaysFromStartedNumber = (
  startedDate: string,
  endDate: string
): number => {
  const currentDate = new Date(startedDate);
  const futureDate = new Date(endDate);

  const oneDay = 1000 * 60 * 60 * 24;

  // Calculating the time difference between two dates
  const diffInTime = futureDate.getTime() - currentDate.getTime();

  // Calculating the no. of days between two dates
  const diffInDays = Math.round(diffInTime / oneDay);

  return diffInDays;
};

export type ItemWithNameAndId = {
  name?: string;
  id?: string;
};

export const createOptions = (
  items: ItemWithNameAndId[]
): Array<OptionTypeBase> =>
  items?.map((o) => {
    return {
      label: o?.name,
      value: o?.id,
    };
  });

export const storageClassesContainOnline = (
  storageClasses: string[]
): boolean => {
  return (
    storageClasses.includes(StorageClass.ONLINE) ||
    storageClasses.includes(StorageClass.ONLINE_INFREQUENT_ACCESS) ||
    storageClasses.includes(StorageClass.NEARLINE)
  );
};

export const getOperatingSystem = (): string => {
  let operatingSystem = "";
  try {
    if (window.navigator.appVersion.indexOf("Win") !== -1) {
      operatingSystem = OperatingSystem.WINDOWS;
    }
    if (window.navigator.appVersion.indexOf("Linux") !== -1) {
      operatingSystem = OperatingSystem.LINUX;
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log(e);
  }

  return operatingSystem;
};

export const convertDateToStringRequest = (value: Date): string => {
  const browserZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formatDate = formatInTimeZone(value, browserZoneName, FORMAT_IN_ZONED);
  return formatDate.replace(" ", "T");
};

export const partStringToTime = (str: string): string => {
  return str.indexOf(":") > -1
    ? str
    : str.substring(0, 2) + ":" + str.substring(2, str.length);
};

export const formatDateTime = (
  value: string,
  formatPattern = "dd/MM/yyyy HH:mm"
  // format with timezone.
): string => formatValidDate(value, formatPattern);

export const formatRegionUrl = (
  region: string,
  apiName = "api",
  httpType?: string
): string => {
  return `${
    httpType ? httpType : "https://"
  }${region}.${apiName}.${API_BASE_URL}`;
};

export const getSubDomain = () => {
  const origin = window.location.origin;
  const domainArr = origin.split("https://");
  const domain = domainArr[1];
  const urlArr = domain?.split(".");
  return urlArr?.length > 0 ? urlArr[0] : "";
};

export const jsStoreReplaceFunction = (
  urlDecode: string,
  subDomain: string
) => {
  if (subDomain.indexOf("clinician") > -1) {
    // CP Case
    return urlDecode?.indexOf("cdn") > -1
      ? urlDecode.replace("cdn.", "") // cnd.clinician.qa.advapacs.com => clinician.qa.advapacs.com
      : urlDecode;
  }
  return urlDecode?.indexOf("cdn") > -1
    ? urlDecode.replace("cdn", subDomain) // cnd.qa.advapacs.com => subdomain.qa.advapacs.com
    : urlDecode;
};

// this function will return format date str or invalid string.
export const formatValidDate = (date?: any, fmt = ddMMyyyy): string => {
  if (!date) return "";
  const validDate = isValid(new Date(date));
  if (validDate) return format(new Date(date), fmt);
  return "Invalid Value";
};

export const convertKBToMB = (num: number) => {
  const dump = 1024 * 1024;
  return Math.floor(num / dump);
};

export const convertToOptionTypeBase = (
  responseList: DropDownResponse<string>[]
): OptionTypeBase[] => {
  return responseList.map((x) => {
    return {
      label: x.name,
      value: x.id,
    };
  });
};

export const getInnerTextFromDoms = (htmlStr: string) => {
  const parser = new DOMParser();
  const dom = parser.parseFromString(htmlStr, "text/html");
  return dom?.body.innerText;
};

export const swapArrayItemWithIndex = <T>(
  array: T[],
  index: number,
  direction: "up" | "down"
): T[] => {
  if (direction === "up" && index > 0) {
    [array[index], array[index - 1]] = [array[index - 1], array[index]];
  } else if (direction === "down" && index < array.length - 1) {
    [array[index], array[index + 1]] = [array[index + 1], array[index]];
  }
  return [...array]; // Return a new array to avoid mutation
};

export const getSessionValueWithRetry = async (
  key: string,
  retries = 3,
  delayMs = 1000
): Promise<string | null> => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    const value = sessionStorage.getItem(key);
    if (value !== null) return value;

    if (attempt < retries) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  return null;
};
